<template>
  <div>
    <div class="row">
      <div class="col-sm-8 col-sm-offset-2">
        <p class="small">
          You have indicated that you are interested in working with U.S. companies via Worksome.
        </p>
        <p class="small">
          Therefore we need to collect some mandatory information from you before you can accept any
          contracts with a U.S. connection (either if you are based in the U.S. or performing work
          in the U.S.).
        </p>
      </div>
    </div>

    <div class="form-group p-t-30">
      <label class="col-md-4 control-label" for="nameInput">Name</label>
      <div class="col-md-6 col-xs-8">
        <input
          id="nameInput"
          v-model="name"
          disabled
          name="us_tax_name"
          type="text"
          required="required"
          class="form-control"
        />
      </div>
    </div>

    <div class="form-group">
      <div class="col-md-4 control-label p-t-0">Tax residence *</div>
      <div class="col-md-6 col-xs-8">
        <div class="checkbox">
          <label>
            <input
              v-model="sameAsAccountDetails"
              name="us_tax_residence_same_as_account_details"
              type="checkbox"
              value="true"
            />
            <i class="input-helper" />
            {{ $t('Same as my Worksome account details') }}
          </label>
        </div>
      </div>
    </div>

    <edit-location
      v-show="!sameAsAccountDetails"
      :persistent-address="{
        fieldName: 'us_tax_residence_address',
        value: residence.address,
      }"
      :persistent-zipcode="{
        fieldName: 'us_tax_residence_zipcode',
        value: residence.zipcode,
      }"
      :persistent-city="{
        fieldName: 'us_tax_residence_city',
        value: residence.city,
      }"
      :persistent-country="{
        fieldName: 'us_tax_residence_country',
        value: residence.country,
      }"
      :persistent-state="{
        fieldName: 'us_tax_residence_state',
        value: residence.state,
      }"
      @update:country="(country) => (selectedCountry = country)"
    />

    <div class="form-group p-t-10">
      <div class="col-md-4 control-label">Select your status *</div>
      <div class="col-md-6 col-xs-8">
        <div class="radio">
          <label>
            <input v-model="isUsPerson" :value="true" type="radio" name="us_person" />
            <i class="input-helper" />
            <WdsPopover placement="top">
              <template #trigger="{ togglePopover }">
                <WdsStack direction-row class="tw-items-center">
                  I am a U.S. tax resident
                  <WdsButtonGhost @click="togglePopover">
                    <i class="c-indigo-500 zmdi zmdi-help" />
                  </WdsButtonGhost>
                </WdsStack>
              </template>
              This applies to U.S. Persons (a citizen or resident of the United States),
              <a
                href="https://www.irs.gov/individuals/international-taxpayers/taxation-of-us-residents"
              >
                read the full criteria of U.S. Persons
              </a>
            </WdsPopover>
          </label>
        </div>
        <div class="radio">
          <label>
            <input
              v-model="isUsPerson"
              :disabled="getResidence.country === 'US' || selectedCountry === 'US'"
              :value="false"
              type="radio"
              name="us_person"
            />
            <i class="input-helper" />
            I am not a U.S. tax resident
          </label>
        </div>
      </div>
    </div>
    <hr />
    <div v-show="isUsPerson">
      <h4 class="text-center m-b-20 m-t-20">{{ $t('W-9') }}</h4>
      <div class="text-center m-b-15">
        Before withdrawing funds, all U.S. persons must provide their W-9 tax information.
      </div>
    </div>
    <div v-show="!isUsPerson">
      <h4 class="text-center m-b-20 m-t-20">{{ $t('W-8BEN') }}</h4>
    </div>

    <div class="form-group p-t-30">
      <label class="col-md-4 control-label" for="usBusinessNameInput">
        Business name / disregarded entity name *
      </label>
      <div class="col-md-6 col-xs-8">
        <input
          id="usBusinessNameInput"
          v-model="businessName"
          name="us_tax_business_name"
          type="text"
          required="required"
          class="form-control"
        />
        <p class="help-block small">
          If you don't have a registered business, enter your full legal name as it appears on your
          tax documents.
        </p>
      </div>
    </div>

    <div v-if="isUsPerson">
      <div class="form-group">
        <label for="usTaxClassification" class="col-md-4 control-label">
          Federal Tax Classification *
        </label>
        <div class="col-md-6">
          <select
            id="usTaxClassification"
            v-model="taxClassification"
            required="required"
            name="us_tax_classification"
            class="form-control"
          >
            <option value="">{{ $t('Choose an option') }}</option>
            <option
              v-for="taxClassificationValue in taxClassifications"
              :key="taxClassificationValue"
              :value="taxClassificationValue"
            >
              {{ taxClassificationValue }}
            </option>
          </select>
        </div>
      </div>

      <div class="form-group p-t-30">
        <div class="col-md-4 control-label">Tax Identification Number Type *</div>
        <div class="col-md-6 col-xs-8">
          <div class="radio">
            <label>
              <input v-model="tinType" :value="'SSN'" type="radio" name="tin_type" />
              <i class="input-helper" />
              <span
                data-toggle="tooltip"
                data-original-title="Your nine-digit Social Security number, which is administered by the U.S. Social Security Administration."
              >
                Social Security Number (SSN)
                <i class="c-indigo-500 zmdi zmdi-help" />
              </span>
            </label>
          </div>
          <div class="radio">
            <label>
              <input v-model="tinType" :value="'EIN'" type="radio" name="tin_type" />
              <i class="input-helper" />
              <span
                data-toggle="tooltip"
                data-original-title="An Employer Identification Number (EIN) is also known as a Federal Tax Identification Number, and is used to identify a business entity. Generally, businesses need an EIN and it can be obtained for free from the Internal Revenue Service."
              >
                Employer Identification Number (EIN)
                <i class="c-indigo-500 zmdi zmdi-help" />
              </span>
            </label>
          </div>
          <div class="row">
            <label class="col-md-2 control-label" for="tinInput">
              <template v-if="tinType === 'SSN'">SSN</template>
              <template v-else>EIN</template>
            </label>
            <div class="col-md-10">
              <input
                id="tinInput"
                v-model="tinValue"
                name="tin"
                type="text"
                required="required"
                class="form-control"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="form-group">
        <div class="col-md-4 control-label">
          Backup withholding
          <br />
          <a class="f-12 c-gray" href="https://www.irs.gov/pub/irs-pdf/fw9.pdf" target="_blank">
            Read about the IRS instructions
          </a>
        </div>
        <div class="col-md-6 col-xs-8">
          <div class="radio">
            <label>
              <input
                v-model="subjectToBackup"
                :value="'subject'"
                type="radio"
                name="us_tax_subject_to_backup"
              />
              <i class="input-helper" />
              I am subject to backup withholding
            </label>
          </div>
          <div class="radio">
            <label>
              <input
                v-model="subjectToBackup"
                :value="'exempt'"
                type="radio"
                name="us_tax_subject_to_backup"
              />
              <i class="input-helper" />
              I am exempt from backup withholding
            </label>
          </div>

          <div v-if="subjectToBackup === 'exempt'" class="p-l-30">
            <div class="radio">
              <label>
                <input
                  v-model="backupExceptDataType"
                  :value="'payee'"
                  type="radio"
                  name="us_tax_backup_exempt_data_type"
                />
                <i class="input-helper" />
                Provide exempt payee code
              </label>
            </div>
            <div class="radio">
              <label>
                <input
                  v-model="backupExceptDataType"
                  :value="'fatca'"
                  type="radio"
                  name="us_tax_backup_exempt_data_type"
                />
                <i class="input-helper" />
                Provide exemption from FATCA reporting code
              </label>
            </div>
            <div v-if="backupExceptDataType !== null" class="row">
              <label class="col-md-2 control-label" for="backupExceptDataInput">
                <template v-if="backupExceptDataType === 'fatca'">FATCA</template>
                <template v-else>PAYEE</template>
              </label>
              <div class="col-md-10">
                <input
                  id="backupExceptDataInput"
                  v-model="backupExceptDataValue"
                  name="us_tax_backup_except_data_value"
                  type="text"
                  required="required"
                  class="form-control"
                />
              </div>
            </div>
          </div>
          <div class="radio">
            <label>
              <input
                v-model="subjectToBackup"
                :value="'no'"
                type="radio"
                name="us_tax_subject_to_backup"
              />
              <i class="input-helper" />
              I am not subject to backup withholding
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-sm-6 col-sm-offset-4 col-xs-12">
        <div class="checkbox">
          <label class="wrap">
            <input v-model="tos" name="us_tax_tos" type="checkbox" value="true" />
            <i class="input-helper" />
            <template v-if="!isUsPerson">
              I certify, under penalties of perjury, that the representations in this Tax
              Certificate are true and correct.
            </template>
            <template v-else>
              <div v-if="subjectToBackup === 'subject'">
                Under penalties of perjury, I certify that:
                <ol type="a">
                  <li>
                    The number shown on this form is my correct taxpayer identification number (or I
                    am waiting for a number to be issued to me); and
                  </li>
                  <li>I am a U.S. citizen or other U.S. person (defined above); and</li>
                  <li>
                    The FATCA code(s) entered on this form (if any) indicating that I am exempt from
                    FATCA reporting is correct.
                  </li>
                </ol>
              </div>
              <div v-else>
                Under penalties of perjury, I certify that:
                <ol>
                  <li>
                    The number shown on this form is my correct taxpayer identification number (or I
                    am waiting for a number to be issued to me); and
                  </li>
                  <li>
                    I am not subject to backup withholding because:
                    <ol>
                      <li>I am exempt from backup withholding, or</li>
                      <li>
                        I have not been notified by the Internal Revenue Service (IRS) that I am
                        subject to backup withholding as a result of a failure to report all
                        interest or dividends, or
                      </li>
                      <li>
                        the IRS has notified me that I am no longer subject to backup withholding;
                        and
                      </li>
                    </ol>
                  </li>
                  <li>I am a U.S. citizen or other U.S. person (defined above); and</li>
                  <li>
                    The FATCA code(s) entered on this form (if any) indicating that I am exempt from
                    FATCA reporting is correct.”
                  </li>
                </ol>
              </div>
            </template>
          </label>
        </div>
      </div>
    </div>

    <div class="form-group">
      <div class="col-sm-6 col-sm-offset-4 col-xs-12">
        <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
        <SubmitButton class="m-t-30" :text="$t('Save')" auto-submit :disabled="!canSave" />
      </div>
    </div>
  </div>
</template>

<script>
import { WdsButtonGhost, WdsPopover, WdsStack } from '@worksome/wds-components'
import EditLocation from '@/components/EditLocation.vue'

export default {
  components: { WdsButtonGhost, WdsStack, WdsPopover, EditLocation },
  props: {
    defaultSameAsAccountDetails: {
      type: Boolean,
      // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
      default: true,
    },
    defaultIsUsPerson: {
      type: Boolean,
      // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
      default: true,
    },
    taxClassifications: {
      type: Array,
      required: true,
    },
    defaultTinType: {
      type: String,
      default: 'SSN',
    },
    defaultTinValue: {
      type: String,
      default: '',
    },
    defaultBusinessName: {
      type: String,
      default: '',
    },
    defaultTaxClassification: {
      type: String,
      required: true,
    },
    defaultTos: {
      type: Boolean,
      default: false,
    },
    defaultResidence: {
      type: Object,
      required: true,
    },
    defaultPrimaryResidence: {
      type: Object,
      required: true,
    },
    defaultName: {
      type: String,
      required: true,
    },
    defaultSubjectToBackup: {
      type: String,
      default: '',
    },
    defaultBackupExceptDataType: {
      type: String,
      default: '',
    },
    defaultBackupExceptDataValue: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      sameAsAccountDetails: this.defaultSameAsAccountDetails,
      isUsPerson: this.defaultIsUsPerson || this.defaultResidence?.country === 'US',
      tinType: this.defaultTinType,
      tinValue: this.defaultTinValue,
      businessName: this.defaultBusinessName,
      taxClassification: this.defaultTaxClassification,
      tos: this.defaultTos,
      residence: this.defaultResidence,
      primaryResidence: this.defaultPrimaryResidence,
      name: this.defaultName,
      subjectToBackup: this.defaultSubjectToBackup,
      backupExceptDataType: this.defaultBackupExceptDataType,
      backupExceptDataValue: this.defaultBackupExceptDataValue,
      selectedCountry: this.defaultResidence?.country ?? undefined,
    }
  },
  computed: {
    canSave() {
      if (!this.tos) {
        return false
      }

      if (!this.businessName) {
        return false
      }

      if (this.isUsPerson === true) {
        if (!this.taxClassification || !this.tinValue) {
          return false
        }
      }

      return true
    },
    infoChanged() {
      return (
        this.sameAsAccountDetails !== this.defaultSameAsAccountDetails ||
        this.isUsPerson !== this.defaultIsUsPerson ||
        this.tinType !== this.defaultTinType ||
        this.tinValue !== this.defaultTinValue ||
        this.businessName !== this.defaultBusinessName ||
        this.taxClassification !== this.defaultTaxClassification ||
        this.tos !== this.defaultTos ||
        this.residence !== this.defaultResidence ||
        this.primaryResidence !== this.defaultPrimaryResidence ||
        this.name !== this.defaultName ||
        this.subjectToBackup !== this.defaultSubjectToBackup ||
        this.backupExceptDataType !== this.defaultBackupExceptDataType ||
        this.backupExceptDataValue !== this.defaultBackupExceptDataValue
      )
    },
    getResidence() {
      return this.sameAsAccountDetails ? this.primaryResidence : this.residence
    },
  },
  watch: {
    infoChanged(newValue) {
      if (newValue === true) {
        this.tos = false
      }
    },
    sameAsAccountDetails(value) {
      if (value && this.getResidence.country === 'US') {
        this.isUsPerson = true
      }
    },
    selectedCountry(value) {
      this.isUsPerson = value === 'US'
    },
  },
}
</script>

<style scoped></style>
